import { Badge, Box, Dialog, IconButton, Menu, Tooltip } from '@mui/material';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  ServiceUserPatientProfile,
  apiUserPatientProfile,
  getPatientColor,
} from 'services/user-patient-profile';

import FileCopyIcon from '@mui/icons-material/FileCopy';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import InfoIcon from '@mui/icons-material/Info';
import clsx from 'clsx';
import { DialogHeading } from 'components/dialog-title';
import { ShortRemark } from 'components/short-remark';
import { Stack } from 'components/stack';
import { TabItem, TabsWrapper } from 'components/tabs-wrapper';
import { APP_ROUTES, PATIENT_CARD_MAIN_TABS } from 'configs/routes';
import { useEffectError, useFetchDynamic } from 'hooks';
import { usePermissions } from 'hooks/use-permissions';
import { useTranslate } from 'hooks/use-translate';
import { useValueQuery } from 'hooks/use-value-query';
import { useIsBelowMd } from 'hooks/use-is-below-md';
import { useAppDispatch } from 'store';
import { actionSetSearchedPatient } from 'store/searched-patients';
import CopyPathToClipboard from 'AurionCR/components/copy-path-to-clipboard';
import { useCurrentUser } from '../../../hooks';
import { patientEmitter } from '../../emitter';
import { usePatientSubscription, usePatientWeights } from '../../hooks';
import { ButtonAddNotebook } from '../button-add-notebook';
import { ButtonBackDoor } from '../button-backdoor';
import { ButtonCall } from '../button-call';
import { ButtonFillHealthSurvey } from '../button-fill-health-survey';
import { ButtonSendSms } from '../button-send-sms';
import { ButtonSendWhatsApp } from '../button-send-whats-app';
import { ButtonTask } from '../button-task';
import { TabAllActivities } from '../tab-all-activities';
import { TabAppointments } from '../tab-appointments';
import { TabCommercial } from '../tab-commercial';
import { TabDocuments } from '../tab-documents';
import { TabGeneral } from '../tab-general';
import { TabMedicalInformation } from '../tab-medical-information';
import { TabMessages } from '../tab-messages';
import { TabWeights } from '../tab-weights';
import style from './index.module.scss';
import { TemplatePatientFullName } from 'components/templates/template-patient-full-name';
import { BadgeEndOfSubscription } from '../badge-end-of-subscription';
import {
  PATIENT_CARD_TASK_QUERY_NAME,
  PATIENT_CARD_CLINICAL_MEETING_QUERY_NAME,
  PATIENT_CARD_SUPPORT_MEETING_QUERY_NAME,
} from '../../helpers';
import { makeFullName } from 'utils/app-helpers';
import { hexToRgb } from '../../../helpers';
import { BadgeSamples } from '../badge-samples';

const QUERY_NAME = 'main-tab';

interface Props {
  itemID: string;
  onClose: () => void;
}

export const Editor = memo<Props>(({ onClose, itemID }) => {
  const { tp, t } = useTranslate();
  const { $isAdmin } = useCurrentUser();
  const dispatch = useAppDispatch();
  // Phase F3 (F3-03, D-69): below md the 24 rem side rail becomes a horizontal,
  // scrollable tab strip above the content; the tabs, the query params and the
  // close are the same, only the orientation flips. Layout is in the module's
  // md query; this is the one behaviour the CSS cannot express.
  const isBelowMd = useIsBelowMd();
  const {
    data: initData,
    isLoading,
    error,
    refetch,
  } = apiUserPatientProfile.useGetPatientForGeneralFormQuery(itemID);
  useEffectError(error);

  const queryTask = useValueQuery<string>({
    name: PATIENT_CARD_TASK_QUERY_NAME,
    destroyOnUnmount: true,
  });
  const querySupportMeeting = useValueQuery<string>({
    name: PATIENT_CARD_SUPPORT_MEETING_QUERY_NAME,
    destroyOnUnmount: true,
  });
  const queryClinicalMeeting = useValueQuery<string>({
    name: PATIENT_CARD_CLINICAL_MEETING_QUERY_NAME,
    destroyOnUnmount: true,
  });

  const { data: subscription, isInactiveSubscription } = usePatientSubscription(itemID);
  const showDietitian = Boolean(subscription && subscription.subscription.visitDietitian);

  const { firstName, lastName, idNumber, shortRemark, mobilePhone } = initData || {};

  const formTitleWrap = useMemo(() => {
    if (!initData) {
      return t('loading');
    }

    const fullName = makeFullName(initData);
    const userInfo = [fullName, idNumber].filter(Boolean).join(' | ');
    const name = (
      <TemplatePatientFullName patient={{ ...initData, fullName }} variant={'h4'}>
        {userInfo}
      </TemplatePatientFullName>
    );

    // N1 (brief pattern C): below md the chips are a line of their own, under
    // the heading's one row (the `titleChips` child below)
    if (isBelowMd) {
      return (
        <Stack spacing={1} className={style.titleBlock}>
          {name}
        </Stack>
      );
    }

    return (
      <Stack
        spacing={1}
        className={style.titleBlock}
        sx={{
          mr: 1,
        }}
      >
        {name}
        {shortRemark && <ShortRemark priority={'high'}>{shortRemark}</ShortRemark>}
        <BadgeEndOfSubscription userPatientProfileID={itemID} />
        <BadgeSamples userPatientProfileID={itemID} />
      </Stack>
    );
  }, [shortRemark, idNumber, t, itemID, initData, isBelowMd]);

  const [triggerGetTasksCount, countResult] = useFetchDynamic(
    ServiceUserPatientProfile.getCountNotDoneTasks,
  );
  useEffect(() => {
    triggerGetTasksCount(itemID);
  }, [triggerGetTasksCount, itemID]);
  useEffect(() => {
    const refresh = () => triggerGetTasksCount(itemID);
    patientEmitter.on('refresh-tasks', refresh);
    return () => {
      patientEmitter.off('refresh-tasks', refresh);
    };
  }, [triggerGetTasksCount, itemID]);

  const { refresh } = usePatientWeights(itemID);

  const onRefreshNotebooks = useCallback(() => {
    patientEmitter.emit('refresh-weight', { patientID: itemID });
    patientEmitter.emit('refresh-notebooks', { patientID: itemID });
    refresh();
  }, [itemID, refresh]);

  const onRefreshSms = useCallback(() => {
    patientEmitter.emit('refresh-sms', { patientID: itemID });
  }, [itemID]);
  const onRefreshTasks = useCallback(() => {
    patientEmitter.emit('refresh-tasks', { patientID: itemID });
  }, [itemID]);

  const notDoneTasks = countResult.data?.count;

  const isAllowToEditCommercials = usePermissions('isAllowToEditCommercials');

  const { value: tab, onChange: onChangeTab } = useValueQuery<PATIENT_CARD_MAIN_TABS>({
    name: QUERY_NAME,
    init: PATIENT_CARD_MAIN_TABS.ALL_ACTIVITIES,
    destroyOnUnmount: true,
  });

  const userPatientProfileSubscriptionID = subscription?.id ?? undefined;

  const tabs = useMemo<TabItem<PATIENT_CARD_MAIN_TABS>[]>(
    () => [
      {
        value: PATIENT_CARD_MAIN_TABS.ALL_ACTIVITIES,
        keepInMemory: false,
        title: (
          <Badge badgeContent={notDoneTasks} color={'error'}>
            <Box
              sx={{
                p: '0 1rem 0 0',
              }}
            >
              {' '}
              {tp('all-activities')}
            </Box>
          </Badge>
        ),
        element: <TabAllActivities notDoneTasks={notDoneTasks} userPatientProfileID={itemID} />,
      },
      {
        value: PATIENT_CARD_MAIN_TABS.GENERAL,
        keepInMemory: false,
        title: (
          <Badge
            badgeContent={
              isInactiveSubscription && (
                <Tooltip title={tp('patient-subscription-is-outdated-tooltip')}>
                  <InfoIcon color={'error'} />
                </Tooltip>
              )
            }
            color={'default'}
            invisible={false}
          >
            <Box
              sx={{
                p: '0 1.2rem 0 0',
              }}
            >
              {' '}
              {tp('general-information')}
            </Box>
          </Badge>
        ),
        element: (
          <TabGeneral
            userPatientProfileID={itemID}
            showDietitian={showDietitian}
            isLoading={isLoading}
          />
        ),
      },
      {
        value: PATIENT_CARD_MAIN_TABS.APPOINTMENTS,
        title: tp('appointments'),
        element: <TabAppointments userPatientProfileID={itemID} />,
      },
      {
        value: PATIENT_CARD_MAIN_TABS.WEIGHTS,
        title: tp('weights'),
        element: <TabWeights userPatientProfileID={itemID} />,
      },
      {
        value: PATIENT_CARD_MAIN_TABS.MEDICAL_INFORMATION,
        title: tp('medical-information'),
        element: <TabMedicalInformation userPatientProfileID={itemID} />,
      },
      ...(isAllowToEditCommercials
        ? [
            {
              value: PATIENT_CARD_MAIN_TABS.COMMERCIAL,
              title: tp('commercial'),
              element: <TabCommercial userPatientProfileID={itemID} />,
            },
          ]
        : []),

      {
        value: PATIENT_CARD_MAIN_TABS.DOCUMENTS,
        title: tp('documents'),
        element: (
          <TabDocuments
            userPatientProfileID={itemID}
            userPatientProfileSubscriptionID={userPatientProfileSubscriptionID}
          />
        ),
      },
      {
        value: PATIENT_CARD_MAIN_TABS.MESSAGES,
        title: tp('communications'),
        element: <TabMessages userPatientProfileID={itemID} />,
      },
    ],
    [
      tp,
      itemID,
      showDietitian,
      isLoading,
      isAllowToEditCommercials,
      notDoneTasks,
      isInactiveSubscription,
      userPatientProfileSubscriptionID,
    ],
  );

  useEffect(() => {
    if (initData) {
      const fullName = [initData?.firstName, initData?.lastName].filter(Boolean).join(' ');
      const searchedPatient = {
        appIdentityUserID: initData.appIdentityUserID,
        fullName,
        id: initData.id,
        idNumber: initData.idNumber,
        mobilePhone: initData.mobilePhone,
        rowIndex: initData.rowIndex,
        shortRemark: initData.shortRemark,
        date: new Date(),
        isSearched: true,
        isActive: initData.isActive,
        onHold: initData.onHold,
      };
      dispatch(actionSetSearchedPatient(searchedPatient));
    }
  }, [initData, dispatch]);

  useEffect(() => {
    const onRefreshGeneral = ({ patientID }: { patientID: string }) => {
      if (patientID !== itemID) return;
      refetch();
    };

    patientEmitter.on('refresh-general-form', onRefreshGeneral);
    return () => {
      patientEmitter.off('refresh-general-form', onRefreshGeneral);
    };
  }, [itemID, refetch]);

  const patient = useMemo(() => ({ firstName, lastName }), [firstName, lastName]);

  // N1 (brief pattern C): below md the heading is one row -- the name block,
  // the add-task (the card's primary action), the call, an overflow for the
  // rest and the close; the action bar row is gone. The overflow stays mounted:
  // the survey's and the notebook's own menus anchor to their buttons in it,
  // and an action's dialog outlives the overflow's closing. The chips are one
  // line under it, the remark cut to fit; a tap opens the line to its full text.
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
  const onMoreOpen = useCallback(
    (e: React.MouseEvent<HTMLElement>) => setMoreAnchor(e.currentTarget),
    [],
  );
  const onMoreClose = useCallback(() => setMoreAnchor(null), []);

  const patientColor = useMemo(() => {
    const color = getPatientColor(initData ?? {});
    if (!color) return undefined;
    const { r, g, b } = hexToRgb(color);
    return `rgba(${r},${g},${b}, 0.2)`;
  }, [initData]);

  const buttonCopy = (
    <CopyPathToClipboard text={APP_ROUTES.PATIENT_LIST({ patient: itemID }).href}>
      <IconButton color={'primary'} aria-label={t('copy-path')}>
        <FileCopyIcon />
      </IconButton>
    </CopyPathToClipboard>
  );
  const buttonSms = (
    <ButtonSendSms
      patient={patient}
      userPatientProfileID={itemID}
      toPhoneNumber={mobilePhone}
      onDone={onRefreshSms}
      meeting={null}
    />
  );
  const buttonWhatsApp = (
    <ButtonSendWhatsApp
      patient={patient}
      userPatientProfileID={itemID}
      toPhoneNumber={mobilePhone}
      onDone={onRefreshSms}
      meeting={null}
    />
  );
  const buttonSurvey = (
    <ButtonFillHealthSurvey
      patient={initData}
      userPatientProfileID={itemID}
      toPhoneNumber={mobilePhone}
    />
  );
  const buttonBackDoor = $isAdmin && <ButtonBackDoor userPatientProfileID={itemID} />;
  const buttonNotebook = (
    <ButtonAddNotebook userPatientProfileID={itemID} onDone={onRefreshNotebooks} />
  );
  const buttonCall = (
    <ButtonCall
      taskID={queryTask.value}
      userPatientProfileID={itemID}
      supportMeetingID={querySupportMeeting.value}
      clinicalMeetingID={queryClinicalMeeting.value}
    />
  );
  const buttonTask = <ButtonTask userPatientProfileID={itemID} onDone={onRefreshTasks} />;

  return (
    <Dialog fullScreen open={true} fullWidth maxWidth={false} onClose={onClose}>
      <DialogHeading
        classes={{
          root: clsx(style.titleRoot),
          close: style.titleClose,
        }}
        style={{ backgroundColor: patientColor }}
        title={formTitleWrap}
        onClose={onClose}
        isLoading={isLoading}
      >
        {isBelowMd ? (
          <>
            <Stack className={style.titleControls} spacing={1}>
              {buttonTask}
              {buttonCall}
              <IconButton
                color={'primary'}
                aria-haspopup={'true'}
                aria-expanded={Boolean(moreAnchor)}
                onClick={onMoreOpen}
              >
                <MoreVertIcon />
              </IconButton>
            </Stack>
            <Menu
              keepMounted
              anchorEl={moreAnchor}
              open={Boolean(moreAnchor)}
              onClose={onMoreClose}
              onClick={onMoreClose}
              classes={{ paper: style.titleMore }}
            >
              {buttonCopy}
              {buttonSms}
              {buttonWhatsApp}
              {buttonSurvey}
              {buttonBackDoor}
              {buttonNotebook}
            </Menu>
            {/* N2 (T3): one line that never wraps; a chip too long for it is
                cut with an ellipsis (the remark's full text is its title) */}
            <div className={style.titleChips}>
              {shortRemark && (
                <ShortRemark priority={'high'} title={shortRemark}>
                  {shortRemark}
                </ShortRemark>
              )}
              <BadgeEndOfSubscription userPatientProfileID={itemID} />
              <BadgeSamples userPatientProfileID={itemID} />
            </div>
          </>
        ) : (
          <Stack className={style.titleControls} spacing={1}>
            <div>{buttonCopy}</div>
            {buttonSms}
            {buttonWhatsApp}
            {buttonSurvey}
            {buttonBackDoor}
            {buttonNotebook}
            {buttonCall}
            {buttonTask}
          </Stack>
        )}
      </DialogHeading>
      <TabsWrapper
        TabsProps={{
          orientation: isBelowMd ? 'horizontal' : 'vertical',
          slotProps: { indicator: { style: { backgroundColor: 'transparent' } } },
        }}
        classes={{
          root: style.tabs,
          tab: style.tab,
          header: style.header,
          content: style.content,
        }}
        tabs={tabs}
        value={tab}
        onChange={onChangeTab}
      />
    </Dialog>
  );
});
